// src/components/UI/NamespaceTranslationsButton.js
//
// Per-page Export/Import translation buttons — the same underlying
// mechanism as the general (all-namespace) Export/Import on the
// Translations admin screen (see Translations.js and
// ExportTranslationsHandler/ImportTranslationsHandler), just scoped to
// ONE page's own namespace so exporting from inside e.g. Countries.js
// yields only "countries" translations instead of every page's.
//
// Drop this into any list page's PageHeader action row next to the
// Add/Search buttons: <NamespaceTranslationsButton namespace="countries" />
// Import is gated by language_edit (same permission Translations.js uses);
// Export is left ungated since it's read-only.
import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { I18nContext, useTranslation } from "../../context/I18nContext";
import { getLanguages, exportTranslations, importTranslations } from "../../services/api";
import { ICONS } from "../../icons";
import Button from "./Button";
import SelectBox from "./SelectBox";
import { notify } from "../../utils/notify";
import { AnimatePresence, motion } from "framer-motion";

export default function NamespaceTranslationsButton({ namespace, size = "md", onImported }) {
  const { t } = useTranslation("common");
  const { reloadNamespace } = useContext(I18nContext);
  const { userPermissions } = useContext(AuthContext);
  const canEdit = userPermissions?.includes("language_edit");
  const token = localStorage.getItem("token");

  const [languages, setLanguages] = useState([]);
  const [transferMode, setTransferMode] = useState(null); // null | "export" | "import"
  const [transferLangCode, setTransferLangCode] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getLanguages(token)
      .then((langs) => setLanguages((langs || []).filter((l) => !l.is_default)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeTransferModal = () => {
    if (transferBusy) return;
    setTransferMode(null);
    setTransferLangCode(null);
    setImportFile(null);
  };

  const handleExport = async () => {
    if (!transferLangCode) return;
    setTransferBusy(true);
    try {
      const blob = await exportTranslations(token, transferLangCode, namespace);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `translations_${namespace}_${transferLangCode}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify.success(t("notify_export_ready", "Export ready"));
      closeTransferModal();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_export_failed", "Failed to export translations"));
    } finally {
      setTransferBusy(false);
    }
  };

  const handleImport = async () => {
    if (!transferLangCode || !importFile) return;
    setTransferBusy(true);
    try {
      const result = await importTranslations(token, transferLangCode, importFile);
      notify.success(
        t("notify_import_success", "Imported {imported}, cleared {deleted}, skipped {skipped}")
          .replace("{imported}", result.imported)
          .replace("{deleted}", result.deleted)
          .replace("{skipped}", result.skipped)
      );
      closeTransferModal();
      reloadNamespace?.(namespace, transferLangCode);
      onImported?.();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_import_failed", "Failed to import translations"));
    } finally {
      setTransferBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" size={size} icon={ICONS.Download} onClick={() => setTransferMode("export")}>
        {t("btn_export", "Export")}
      </Button>
      {canEdit && (
        <Button variant="outline" size={size} icon={ICONS.Install} onClick={() => setTransferMode("import")}>
          {t("btn_import", "Import")}
        </Button>
      )}

      <AnimatePresence>
        {transferMode && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeTransferModal}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 15 }}
              className="relative bg-white rounded-[2.5rem] p-8 max-w-lg w-full shadow-2xl z-10"
            >
              <h3 className="text-xl font-black text-gray-900 mb-1 capitalize">
                {transferMode === "export"
                  ? t("export_page_translations_title", "Export Page Translations")
                  : t("import_page_translations_title", "Import Page Translations")}
              </h3>
              <p className="text-gray-400 text-sm mb-6">
                {transferMode === "export"
                  ? t("export_page_translations_desc", "Downloads this page's own text for the language you pick — filled in wherever a translation already exists, blank everywhere else.")
                  : t("import_page_translations_desc", "Pick the language the file is for, then upload a CSV (built by Export, or matching its namespace/key/translation columns) to bulk-save every row for this page.")}
              </p>

              {languages.length === 0 ? (
                <div className="text-center text-gray-400 py-6 italic">
                  {t("transfer_no_languages", "No additional languages yet — add one from the Languages page first.")}
                </div>
              ) : (
                <div className="space-y-4">
                  <SelectBox
                    label={t("language_picker_title", "Language")}
                    name="namespace_transfer_language"
                    options={languages.map((l) => ({ value: l.code, label: l.name }))}
                    value={transferLangCode}
                    onChange={setTransferLangCode}
                    placeholder={t("transfer_language_placeholder", "Choose a language…")}
                  />

                  {transferMode === "import" && (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full text-sm text-gray-600 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-200 hover:border-[var(--primary-color)] transition-all px-4 py-3 text-start"
                      >
                        {importFile ? importFile.name : t("transfer_choose_csv_file", "Choose a CSV file…")}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 mt-8">
                <Button variant="secondary" onClick={closeTransferModal} className="flex-1 !rounded-2xl" disabled={transferBusy}>
                  {t("btn_cancel", "Cancel")}
                </Button>
                {languages.length > 0 && (
                  <Button
                    variant="primary"
                    onClick={transferMode === "export" ? handleExport : handleImport}
                    isLoading={transferBusy}
                    className="flex-1 !rounded-2xl"
                    disabled={!transferLangCode || (transferMode === "import" && !importFile)}
                  >
                    {transferMode === "export" ? t("btn_export", "Export") : t("btn_import", "Import")}
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
